import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Plus, Pencil, Trash2, Loader2, ExternalLink } from "lucide-react";
import * as adminApi from "@/api/admin";
import type { LandingPageSummary } from "./types";

interface LandingPagesViewProps {
  onEdit: (slug: string) => void;
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function LandingPagesView({ onEdit }: LandingPagesViewProps) {
  const [pages, setPages] = useState<LandingPageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);

  function load() {
    setLoading(true);
    setError(false);
    adminApi
      .listLandingPages()
      .then(setPages)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const slug = newSlug.trim() || slugify(newTitle);
    if (!newTitle.trim() || !slug) return;
    setSaving(true);
    setCreateError(null);
    try {
      const page = await adminApi.createLandingPage(slug, newTitle.trim());
      setCreating(false);
      setNewTitle("");
      setNewSlug("");
      setSlugTouched(false);
      onEdit(page.slug);
    } catch {
      setCreateError("Couldn't create the page — that slug may already be in use.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(slug: string) {
    if (!confirm(`Delete landing page "${slug}"? This can't be undone.`)) return;
    setDeletingSlug(slug);
    try {
      await adminApi.deleteLandingPage(slug);
      setPages((prev) => prev.filter((p) => p.slug !== slug));
    } catch {
      alert("Failed to delete the page.");
    } finally {
      setDeletingSlug(null);
    }
  }

  return (
    <div className=" space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load landing pages.</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        {!creating && (
          <Button
            onClick={() => setCreating(true)}
            className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white"
          >
            <Plus size={14} className="mr-1.5" /> New landing page
          </Button>
        )}
      </div>

      {creating && (
        <form
          onSubmit={handleCreate}
          className="bg-white border border-gray-200 rounded-lg p-5 space-y-4"
        >
          {createError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Title</label>
              <Input
                value={newTitle}
                onChange={(e) => {
                  setNewTitle(e.target.value);
                  if (!slugTouched) setNewSlug(slugify(e.target.value));
                }}
                placeholder="Spring Promo"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">URL slug</label>
              <Input
                value={newSlug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setNewSlug(slugify(e.target.value));
                }}
                placeholder="spring-promo"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCreating(false);
                setCreateError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !newTitle.trim()}
              className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Create & edit"}
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full rounded-lg" />
          <Skeleton className="h-14 w-full rounded-lg" />
        </div>
      ) : pages.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-12 border border-dashed border-gray-200 rounded-lg">
          No landing pages yet.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
          {pages.map((page) => (
            <div key={page.slug} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{page.title}</p>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      page.is_published
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {page.is_published ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate">/l/{page.slug}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {page.is_published ? (
                  <Button variant="ghost" size="sm" asChild>
                    <a
                      href={`/l/${page.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View live page"
                    >
                      <ExternalLink size={14} className="mr-1.5" /> View
                    </a>
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled
                    title="Publish the page first to view it live"
                    className="text-gray-300"
                  >
                    <ExternalLink size={14} className="mr-1.5" /> View
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => onEdit(page.slug)}>
                  <Pencil size={14} className="mr-1.5" /> Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  disabled={deletingSlug === page.slug}
                  onClick={() => handleDelete(page.slug)}
                >
                  {deletingSlug === page.slug ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
